import type { MediaAsset } from "@eaub/shared";
import { formatBytes } from "../lib/format";
import { CopyButton } from "./CopyButton";

interface MediaGridProps {
  assets: MediaAsset[];
  /** When provided, cards become selectable and clicking toggles selection. */
  selectedIds?: string[];
  onToggle?: (asset: MediaAsset) => void;
  /** Show copy id / copy url controls (library view). */
  showCopy?: boolean;
}

function isImage(contentType: string): boolean {
  return contentType.startsWith("image/");
}

export function MediaGrid({ assets, selectedIds, onToggle, showCopy = false }: MediaGridProps) {
  const selectable = typeof onToggle === "function";
  const selected = new Set(selectedIds ?? []);

  if (assets.length === 0) {
    return <p className="muted">No media uploaded yet.</p>;
  }

  return (
    <ul className="media-grid">
      {assets.map((asset) => {
        const isSelected = selected.has(asset.id);
        return (
          <li
            key={asset.id}
            className={
              "media-card" +
              (selectable ? " media-card--selectable" : "") +
              (isSelected ? " media-card--selected" : "")
            }
          >
            {selectable ? (
              <button
                type="button"
                className="media-card__select"
                aria-pressed={isSelected}
                onClick={() => onToggle?.(asset)}
              >
                <div className="media-card__thumb">
                  {isImage(asset.contentType) ? (
                    <img src={asset.url} alt={asset.filename} loading="lazy" />
                  ) : (
                    <span className="media-card__file" aria-hidden="true">
                      {asset.contentType || "file"}
                    </span>
                  )}
                  {isSelected ? <span className="media-card__check">✓</span> : null}
                </div>
              </button>
            ) : (
              <div className="media-card__thumb">
                {isImage(asset.contentType) ? (
                  <img src={asset.url} alt={asset.filename} loading="lazy" />
                ) : (
                  <span className="media-card__file" aria-hidden="true">
                    {asset.contentType || "file"}
                  </span>
                )}
              </div>
            )}

            <div className="media-card__meta">
              <span className="media-card__name" title={asset.filename}>
                {asset.filename}
              </span>
              <span className="media-card__size">{formatBytes(asset.size)}</span>
            </div>

            {showCopy ? (
              <div className="media-card__actions">
                <CopyButton value={asset.id} label="Copy ID" />
                <CopyButton value={asset.url} label="Copy URL" />
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
